/**
 * Utilities pure per formattazione/parsing di periodi (anno/quarter/mese/settimana/giorno).
 * Estratte da PaginaPeriodo.jsx (refactor P2 Feb 2026).
 *
 * Tutto stateless, nessuna dipendenza React.
 */

export const MONTH_LABELS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
export const MONTH_SHORT_IT = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
];
export const DAY_SHORT_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

/** confidenceBucket(0.85) → 'alta', confidenceBucket(null) → null */
export function confidenceBucket(c) {
  if (c === null || c === undefined) return null;
  const n = Number(c);
  return n >= 0.7 ? 'alta' : n >= 0.4 ? 'media' : 'bassa';
}

/** Lunedì→Domenica di una ISO week 'YYYY-Www'. Ritorna [Date(mon), Date(sun)] o null. */
export function isoWeekRange(label) {
  if (!label || !label.includes('-W')) return null;
  const [y, w] = label.split('-W').map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
  const mon = new Date(week1Mon);
  mon.setUTCDate(week1Mon.getUTCDate() + 7 * (w - 1));
  const sun = new Date(mon);
  sun.setUTCDate(mon.getUTCDate() + 6);
  return [mon, sun];
}

/** "Settimana W24 · 8–14 giu" (compatta, per header MIT group). */
export function labelSettimana(week) {
  const r = isoWeekRange(week);
  if (!r) return week;
  const [mon, sun] = r;
  const monStr = `${mon.getUTCDate()}`;
  const sunStr = `${sun.getUTCDate()} ${MONTH_SHORT_IT[sun.getUTCMonth()]}`;
  const monthLeft = mon.getUTCMonth() !== sun.getUTCMonth() ? ` ${MONTH_SHORT_IT[mon.getUTCMonth()]}` : '';
  const wn = week.split('-W')[1];
  return `Settimana W${wn} · ${monStr}${monthLeft}–${sunStr}`;
}

/** "Mar 14 Giu" da un ISO 'YYYY-MM-DD'. */
export function labelGiorno(iso) {
  if (!iso || iso.length < 10) return iso;
  const d = new Date(iso + 'T00:00:00Z');
  return `${DAY_SHORT_IT[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_SHORT_IT[d.getUTCMonth()]}`;
}

/** Titolo lungo a seconda della granularità ("Giugno 2026", "Q2 · 2026", ecc.) */
export function labelForGran(gran, label) {
  if (gran === 'anno') return `Anno ${label}`;
  if (gran === 'quarter') {
    const [q, y] = label.split('-');
    return `${q} · ${y}`;
  }
  if (gran === 'mese') {
    const [y, m] = label.split('-');
    return `${MONTH_LABELS_IT[parseInt(m, 10) - 1]} ${y}`;
  }
  if (gran === 'settimana') {
    const [y, w] = label.split('-W');
    const r = isoWeekRange(label);
    if (r) {
      const [mon, sun] = r;
      const monDay = mon.getUTCDate();
      const sunDay = sun.getUTCDate();
      const monMonth = mon.getUTCMonth();
      const sunMonth = sun.getUTCMonth();
      if (monMonth === sunMonth) {
        return `Settimana ${w} · ${y} — ${monDay}–${sunDay} ${MONTH_LABELS_IT[monMonth].toLowerCase()}`;
      }
      return `Settimana ${w} · ${y} — ${monDay} ${MONTH_SHORT_IT[monMonth].toLowerCase()}–${sunDay} ${MONTH_SHORT_IT[sunMonth].toLowerCase()}`;
    }
    return `Settimana ${w} · ${y}`;
  }
  if (gran === 'giorno') {
    const [y, m, d] = label.split('-');
    return `${d}/${m}/${y}`;
  }
  return label;
}

/** "1.234" / "1.234,5 €" — locale it-IT, padding unità opzionale. */
export function fmtNum(v, unita) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n)
    ? n.toLocaleString('it-IT')
    : n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
  return unita ? `${s} ${unita}` : s;
}
